import { Component } from 'react';
import moment from 'moment.lib';
import WrapIf from 'wrap-if.react';
import { BodyText, Button, Dropdown, Flex, Headline, IconButton, IconV2, Tooltip, TextButton, LayoutBox } from '@bamboohr/fabric';

import { consume } from '../../store/context';
import { CLOCK_DISABLED_FUTURE_ENTRY } from 'time-tracking/constants';
import {
	lastActionText,
	clockButtonText,
	formatTime,
	getPaidHolidayText,
	forgotToClockOut,
	getHasFutureEntry,
	convertNormalizedProjectsToDropdownItems,
	getProjectAndTaskId,
} from 'time-tracking/utils';
import TimeSpinner from 'time-tracking/components/time-spinner.react';
import ChooseClockOutDateModal from 'time-tracking/modals/choose-clock-out-date';
import ForgotClockOutWarningModal from 'time-tracking/modals/forgot-clock-out-warning';
import AddNoteModal from 'time-tracking/modals/add-note';
import { ifFeature, isEnabled } from '@bamboohr/utils/lib/feature';
import { PoMicroFrontend } from 'micro-frontend.react';

const isAnybodyClockEnabled = isEnabled('anybodyClock');
const geolocationEnabled = isEnabled('timeTrackingGeolocation');

export class ClockInOut extends Component {
	constructor(props) {
		super(props);
		this.userCanEdit = props.workflow.userCanEdit || false;

		this.state = {
			requesting: false,
			preventingDoubleClick: false,
			takeBreakModal: {
				open: false,
				render: false,
			}
		};
	}

	_toggleClocking = (projectId, taskId) => {
		const { preventingDoubleClick } = this.state;
		const { clock, clockIn, clockOut, employee, refreshTimesheet } = this.props;
		const forgotClockOut = clock ? forgotToClockOut(clock.latest) : false;

		if (this.userCanEdit && forgotClockOut && clock.latest) {
			ChooseClockOutDateModal(employee.id, clock.latest)
				.then(refreshTimesheet);
			return;
		} if (!this.userCanEdit && forgotClockOut) {
			ForgotClockOutWarningModal(clockOut, employee.id, () => { this._toggleRequesting(); })
				.then(() => {
					this._toggleRequesting();
				});
			return;
		}

		if (preventingDoubleClick) {
			return;
		}

		if (window.ASSUMED_USER) {
			window.disabledForPermsTest();
			return;
		}

		this._preventDoubleClick();
		this._toggleRequesting();

		const clockInOrOut = (clock.clockedIn ? clockOut : clockIn);
		clockInOrOut(employee.id, projectId, taskId)
			.finally(this._toggleRequesting);
	};

	_toggleRequesting = () => {
		this.setState({
			requesting: !this.state.requesting,
		});
	};

	_preventDoubleClick = () => {
		this.setState({ preventingDoubleClick: true });
		setTimeout(() => {
			this.setState({ preventingDoubleClick: false });
		}, 400);
	};

	_handleAddNoteClick = () => {
		const { clock, employee, refreshTimesheet, workflow, isViewOnly, projectsWithTasks } = this.props;

		const projectData = {
			projectId: clock.latest.projectId,
			projectName: clock.latest.projectName,
			taskId: clock.latest.taskId,
			taskName: clock.latest.taskName,
		};

		const noteModalData = {
			canEdit: workflow.userCanEdit,
			clock,
			employeeId: employee.id,
			note: clock.latest.note,
			projectData,
			projectsWithTasks,
			today: moment.tz(clock.latest.start, clock.latest.timezone),
			timezone: clock.latest.timezone,
			type: 'clock',
			isViewOnly,
		};
		AddNoteModal(noteModalData)
			.then(refreshTimesheet);
	};

	_getCanTakeBreak() {
		const { clock, availableBreaks } = this.props;
		
		return clock.clockedIn && availableBreaks?.length > 0;
	}

	_handleProjectDropdownClick = (value) => {
		const [projectId, taskId] = getProjectAndTaskId(value);
		this._toggleClocking(projectId, taskId);
	};

	_hasFutureEntry() {
		const {
			clock: { latest },
			serverTime,
		} = this.props;
		return getHasFutureEntry(latest, serverTime);
	}

	_renderClockButton() {
		const {
			clock,
			clock: { clockedIn, latest },
		} = this.props;
		const { requesting } = this.state;

		const clockIcon = clockedIn ? 'circle-stop-solid' : 'stopwatch-regular';
		const hasFutureEntry = this._hasFutureEntry();
		const forgotClockOut = clock ? forgotToClockOut(latest) : false;
		let biIdClockState = clockedIn ? 'clock-out' : 'clock-in';

		if (forgotClockOut) {
			biIdClockState = 'verify-and-clock-out';
		}

		return(
			<Button
				color={ifFeature('TIME_MEAL_REST_BREAKS', this._getCanTakeBreak() ? 'secondary' : 'primary')}
				data-bi-id={`my-info-timesheet-${biIdClockState}-button`}
				disabled={requesting || hasFutureEntry}
				onClick={() => this._toggleClocking()}
				processing={requesting}
				startIcon={clockIcon}
				type='button'
				width={100}
			>
				{clockButtonText(clock.clockedIn, requesting, forgotClockOut, this.userCanEdit)}
			</Button>
		);
	}

	_handleTakeBreakClick() {
		const { availableBreaks } = this.props;

		if (availableBreaks?.length === 1) {
			window.setMessage('Take Only Available Break: Not yet implemented');
			return;
		}
		
		this.setState({
			takeBreakModal: {
				open: true,
				render: true,
			}
		});
	}

	_renderTakeBreakButton() {
		const { requesting } = this.state;
		const hasFutureEntry = this._hasFutureEntry();	

		return(
			<Button
				data-bi-id="home-timesheet-widget-take-break-button"
				disabled={requesting || hasFutureEntry}
				onClick={() => (
					this._handleTakeBreakClick()
				)}
				processing={requesting}
				startIcon="mug-hot-regular"
				type='button'
				width={100}
			>
				{ $.__('Take A Break') }
			</Button>
		);
	}

	_renderProjectTrackingButton() {
		const {
			allowClockActions,
			clock,
			isOwnTimesheet,
			projectsWithTasks,
			isSummaryView,
		} = this.props;
		const { requesting } = this.state;

		const dropdownItems = convertNormalizedProjectsToDropdownItems(projectsWithTasks);
		const hasFutureEntry = this._hasFutureEntry();
		
		if( allowClockActions === false ){
			return null;
		}

		const canTakeBreak = this._getCanTakeBreak();

		return (
			<WrapIf
				condition={ isOwnTimesheet && hasFutureEntry }
				wrap={(content) => <Tooltip content={CLOCK_DISABLED_FUTURE_ENTRY}>{content}</Tooltip>}
			>
				<Flex
					alignItems='center'
					alignSelf='stretch'
					flexWrap={
						ifFeature('TIME_MEAL_REST_BREAKS', isSummaryView && canTakeBreak ? 'wrap' : undefined)
					}
					gap={1}
					marginTop={1.75}
				>
					{ifFeature('TIME_MEAL_REST_BREAKS',
						canTakeBreak ? <Flex width="100%">{this._renderTakeBreakButton()}</Flex> : null
					)}
					{ifFeature('TIME_MEAL_REST_BREAKS', 
						<Flex width={!isSummaryView && canTakeBreak ? 'max-content' : '100%'}>
							{this._renderClockButton()}
						</Flex>,
						this._renderClockButton()
					)}
					{/* @startCleanup TIME_MEAL_REST_BREAKS */}
					{ifFeature('TIME_MEAL_REST_BREAKS', null, clock.clockedIn && (
						<IconButton
							ariaLabel={$.__('Add note')}
							color='secondary'
							icon='file-plus-regular'
							onClick={this._handleAddNoteClick}
							type='button'
						/>
					))}
					{/* @endCleanup TIME_MEAL_REST_BREAKS */}
					{!clock.clockedIn && projectsWithTasks.allIds.length > 0 ? (
						<Dropdown
							ButtonProps={{
								ariaLabel: $.__('Clock in to project or task'),
								color: 'secondary',
								processing: requesting,
							}}
							isDisabled={requesting || hasFutureEntry}
							items={dropdownItems}
							onSelect={this._handleProjectDropdownClick}
							showSearch='auto'
							type='icon'
						/>
					) : null}
				</Flex>
			</WrapIf>
		);
	}

	render() {
		const {
			clock,
			focusedTimesheet,
			getToday,
			serverTime,
			isOwnTimesheet,
			requesting,
			availableBreaks,
		} = this.props;

		const {
			takeBreakModal,
		} = this.state;

		// Geolocation data
		const {
			clockInLatitude,
			clockInLongitude,
			clockOutLatitude,
			clockOutLongitude,
		} = clock.latest || {};

		const locationExists = !!((clockInLatitude && clockInLongitude) || (clockOutLatitude && clockOutLongitude));
		const today = getToday();
		const { holidayPayType } = focusedTimesheet;
		const { showEditActions } = focusedTimesheet;
		const forgotClockOut = clock ? forgotToClockOut(clock.latest) : false;
		const spinnerFillColor = forgotClockOut ? 'orange' : null; 

		const holidays = today?.holidays?.map((holiday) => {
			if (holiday.paidHours !== null) {
				const holidayMessage = getPaidHolidayText(holiday);
				return <BodyText key={holiday.name}>{holidayMessage}</BodyText>;
			}
		}) || [];

		return (
			<Flex alignItems='center' flexDirection='column' gap={1.25} textAlign='center'>
				<BodyText size='large' weight='bold'>
					{clock.clockedIn ? $.__('Clocked In') : $.__('Not Clocked In')}
				</BodyText>
				{clock.clockedIn ? (					
					<Flex alignItems='center'>
						<Headline size='medium' >
							<BodyText color={forgotClockOut ? 'warning' : 'success'}>
							{formatTime(clock.earned + clock.midnightTotals)}
							</BodyText>
						</Headline>
						<TimeSpinner fillColor={spinnerFillColor} />
						{ifFeature('TIME_MEAL_REST_BREAKS', 
							<LayoutBox marginLeft={1}>
								<TextButton disabled={requesting} onClick={this._handleAddNoteClick}>{$.__('Edit')}</TextButton>
							</LayoutBox>
						)}
					</Flex>
				) : (
					<Headline color='primary' size='medium'>
						{$.__('%1$s Today', formatTime(today.hours || 0))}
					</Headline>
				)}
				{clock.latest || clock.clockedIn ? (
					<Flex alignItems='center' gap={0.75} justifyContent='center'>
						<BodyText color='neutral-strong' size='small'>
							{clock.latest && lastActionText(clock.latest, serverTime)}
						</BodyText>
						{clock.clockedIn && clock.latest.note && (
							<IconV2 color='neutral-medium' name='file-solid' size={12} />
						)}
						{geolocationEnabled && clock.clockedIn && locationExists && (
							<IconV2 color='neutral-medium' name='location-dot-solid' size={12} />
						)}
					</Flex>
				) : null}
				{holidayPayType === 'normal' && holidays.length > 0 && holidays}
				{(isOwnTimesheet || (isAnybodyClockEnabled && showEditActions)) && this._renderProjectTrackingButton()}				
				{ifFeature('TIME_MEAL_REST_BREAKS',
					<PoMicroFrontend
						breaks={availableBreaks}
						isOpen={takeBreakModal.open}
						loadingSuspense={false}
						onCloseComplete={() => (
							this.setState({
								takeBreakModal: { isOpen: false, render: false } 
							})
						)}
						onRequestClose={() => (
							this.setState({
								takeBreakModal: { isOpen: false, render: true }
							})
						)}
						route='/time-tracking/take-break-modal'
					/>
				)}
			</Flex>
		);
	}
}


export default consume([
	// Properties
	'allowClockActions',
	'employee',
	'isOwnTimesheet',
	'clock',
	'serverTime',
	'focusedTimesheet.holidayPayType',
	'focusedTimesheet.showEditActions',
	'workflow.userCanEdit',
	'isViewOnly',
	'projectsWithTasks',
	'availableBreaks',
	'isSummaryView',
	// Actions
	'clockIn',
	'clockOut',
	'refreshTimesheet',
	// Queries
	'getToday',
], ClockInOut);
